/**
 * gallerySlice — Redux Toolkit slice for admin CMS gallery management (CON-CMS-004).
 *
 * Kept as a separate slice from cmsSlice to keep each slice focused and to
 * avoid merge conflicts with sibling CMS tickets (CON-CMS-002, CON-CMS-003).
 *
 * State shape
 * -----------
 * images: GalleryImageDTO[]
 * loading: Record<GalleryLoadingKey, boolean>
 * error: Record<GalleryLoadingKey, string | null>
 *
 * Thunks
 * ------
 * - fetchGallery()                           — GET /api/v1/admin/cms/gallery
 * - createGalleryImage(payload)              — POST /api/v1/admin/cms/gallery
 * - updateGalleryImage({ id, ...payload })   — PATCH /api/v1/admin/cms/gallery/:id
 * - deleteGalleryImage(id)                   — DELETE /api/v1/admin/cms/gallery/:id
 * - reorderGallery(ordered_ids)              — PATCH /api/v1/admin/cms/gallery/reorder
 * - bulkDeleteGallery(ids)                   — DELETE /api/v1/admin/cms/gallery/bulk-delete
 * - bulkChangeCategoryGallery({ids,category})— PATCH /api/v1/admin/cms/gallery/bulk-change-category
 *
 * `reorderGallery` applies an optimistic local reorder and reverts on failure.
 *
 * Per `.claude/rules/frontend-data-flow.md` §1, capsules NEVER import this
 * slice; only container views do.
 */
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { galleryAdminApiClient } from '@dfq/shared/api/admin/gallery';
import { isApiError } from '@dfq/shared/api/_envelope';
import type {
  BulkChangeCategoryPayload,
  CreateGalleryImagePayload,
  GalleryImageCategory,
  GalleryImageDTO,
  UpdateGalleryImagePayload,
} from '@dfq/shared/types/cms';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export type GalleryLoadingKey =
  | 'fetchGallery'
  | 'createImage'
  | 'updateImage'
  | 'deleteImage'
  | 'reorder'
  | 'bulkDelete'
  | 'bulkChangeCategory';

export interface GalleryState {
  images: GalleryImageDTO[];
  loading: Record<GalleryLoadingKey, boolean>;
  error: Record<GalleryLoadingKey, string | null>;
}

const initialState: GalleryState = {
  images: [],
  loading: {
    fetchGallery: false,
    createImage: false,
    updateImage: false,
    deleteImage: false,
    reorder: false,
    bulkDelete: false,
    bulkChangeCategory: false,
  },
  error: {
    fetchGallery: null,
    createImage: null,
    updateImage: null,
    deleteImage: null,
    reorder: null,
    bulkDelete: null,
    bulkChangeCategory: null,
  },
};

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const fetchGallery = createAsyncThunk(
  'gallery/fetchGallery',
  async () => galleryAdminApiClient.fetchGallery(),
);

export const createGalleryImage = createAsyncThunk(
  'gallery/createImage',
  async (payload: CreateGalleryImagePayload, { rejectWithValue }) => {
    try {
      return await galleryAdminApiClient.createImage(payload);
    } catch (err) {
      if (isApiError(err)) return rejectWithValue(err.message);
      const msg = err instanceof Error ? err.message : 'Failed to create image';
      return rejectWithValue(msg);
    }
  },
);

export const updateGalleryImage = createAsyncThunk(
  'gallery/updateImage',
  async (
    { id, ...payload }: { id: number } & UpdateGalleryImagePayload,
    { rejectWithValue },
  ) => {
    try {
      return await galleryAdminApiClient.updateImage(id, payload);
    } catch (err) {
      if (isApiError(err)) return rejectWithValue({ id, message: err.message });
      const msg = err instanceof Error ? err.message : 'Failed to update image';
      return rejectWithValue({ id, message: msg });
    }
  },
);

export const deleteGalleryImage = createAsyncThunk(
  'gallery/deleteImage',
  async (id: number, { rejectWithValue }) => {
    try {
      await galleryAdminApiClient.deleteImage(id);
      return id;
    } catch (err) {
      if (isApiError(err)) return rejectWithValue(err.message);
      const msg = err instanceof Error ? err.message : 'Failed to delete image';
      return rejectWithValue(msg);
    }
  },
);

export const reorderGallery = createAsyncThunk(
  'gallery/reorder',
  async (orderedIds: number[], { rejectWithValue }) => {
    try {
      await galleryAdminApiClient.reorder({ ordered_ids: orderedIds });
      return orderedIds;
    } catch (err) {
      if (isApiError(err)) return rejectWithValue(err.message);
      const msg = err instanceof Error ? err.message : 'Failed to reorder gallery';
      return rejectWithValue(msg);
    }
  },
);

export const bulkDeleteGallery = createAsyncThunk(
  'gallery/bulkDelete',
  async (ids: number[], { rejectWithValue }) => {
    try {
      await galleryAdminApiClient.bulkDelete({ ids });
      return ids;
    } catch (err) {
      if (isApiError(err)) return rejectWithValue(err.message);
      const msg = err instanceof Error ? err.message : 'Failed to delete images';
      return rejectWithValue(msg);
    }
  },
);

export const bulkChangeCategoryGallery = createAsyncThunk(
  'gallery/bulkChangeCategory',
  async (payload: BulkChangeCategoryPayload, { rejectWithValue }) => {
    try {
      await galleryAdminApiClient.bulkChangeCategory(payload);
      return payload;
    } catch (err) {
      if (isApiError(err)) return rejectWithValue(err.message);
      const msg = err instanceof Error ? err.message : 'Failed to change category';
      return rejectWithValue(msg);
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const gallerySlice = createSlice({
  name: 'gallery',
  initialState,
  reducers: {
    /** Optimistic local reorder — applied immediately on drag-end. */
    setImages(state, action: PayloadAction<GalleryImageDTO[]>) {
      state.images = action.payload;
    },
    /** Append images created via bulk upload (one per file completion). */
    appendImage(state, action: PayloadAction<GalleryImageDTO>) {
      state.images.push(action.payload);
    },
    /** Patch a single image in-place (optimistic caption/toggle updates). */
    patchImage(state, action: PayloadAction<GalleryImageDTO>) {
      const idx = state.images.findIndex((img) => img.id === action.payload.id);
      if (idx !== -1) {
        state.images[idx] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    // ── fetchGallery ──────────────────────────────────────────────────────────
    builder
      .addCase(fetchGallery.pending, (state) => {
        state.loading.fetchGallery = true;
        state.error.fetchGallery = null;
      })
      .addCase(fetchGallery.fulfilled, (state, action) => {
        state.loading.fetchGallery = false;
        state.images = action.payload.images;
      })
      .addCase(fetchGallery.rejected, (state, action) => {
        state.loading.fetchGallery = false;
        state.error.fetchGallery = action.error.message ?? 'Failed to fetch gallery';
      });

    // ── createGalleryImage ────────────────────────────────────────────────────
    builder
      .addCase(createGalleryImage.pending, (state) => {
        state.loading.createImage = true;
        state.error.createImage = null;
      })
      .addCase(createGalleryImage.fulfilled, (state, action) => {
        state.loading.createImage = false;
        state.images.push(action.payload);
      })
      .addCase(createGalleryImage.rejected, (state, action) => {
        state.loading.createImage = false;
        state.error.createImage =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to create image';
      });

    // ── updateGalleryImage ────────────────────────────────────────────────────
    builder
      .addCase(updateGalleryImage.pending, (state) => {
        state.loading.updateImage = true;
        state.error.updateImage = null;
      })
      .addCase(updateGalleryImage.fulfilled, (state, action) => {
        state.loading.updateImage = false;
        const idx = state.images.findIndex((img) => img.id === action.payload.id);
        if (idx !== -1) {
          state.images[idx] = action.payload;
        }
      })
      .addCase(updateGalleryImage.rejected, (state, action) => {
        state.loading.updateImage = false;
        const payload = action.payload as { id: number; message: string } | undefined;
        state.error.updateImage =
          payload?.message ?? action.error.message ?? 'Failed to update image';
      });

    // ── deleteGalleryImage ────────────────────────────────────────────────────
    builder
      .addCase(deleteGalleryImage.pending, (state) => {
        state.loading.deleteImage = true;
        state.error.deleteImage = null;
      })
      .addCase(deleteGalleryImage.fulfilled, (state, action) => {
        state.loading.deleteImage = false;
        state.images = state.images.filter((img) => img.id !== action.payload);
      })
      .addCase(deleteGalleryImage.rejected, (state, action) => {
        state.loading.deleteImage = false;
        state.error.deleteImage =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to delete image';
      });

    // ── reorderGallery ────────────────────────────────────────────────────────
    builder
      .addCase(reorderGallery.pending, (state) => {
        state.loading.reorder = true;
        state.error.reorder = null;
      })
      .addCase(reorderGallery.fulfilled, (state, action) => {
        state.loading.reorder = false;
        // Re-sort images array to match the server-acknowledged order
        const idxMap = new Map(action.payload.map((id, i) => [id, i]));
        state.images = [...state.images].sort(
          (a, b) => (idxMap.get(a.id) ?? 0) - (idxMap.get(b.id) ?? 0),
        );
      })
      .addCase(reorderGallery.rejected, (state, action) => {
        state.loading.reorder = false;
        state.error.reorder =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to reorder gallery';
      });

    // ── bulkDeleteGallery ─────────────────────────────────────────────────────
    builder
      .addCase(bulkDeleteGallery.pending, (state) => {
        state.loading.bulkDelete = true;
        state.error.bulkDelete = null;
      })
      .addCase(bulkDeleteGallery.fulfilled, (state, action) => {
        state.loading.bulkDelete = false;
        const deleted = new Set(action.payload);
        state.images = state.images.filter((img) => !deleted.has(img.id));
      })
      .addCase(bulkDeleteGallery.rejected, (state, action) => {
        state.loading.bulkDelete = false;
        state.error.bulkDelete =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to delete images';
      });

    // ── bulkChangeCategoryGallery ─────────────────────────────────────────────
    builder
      .addCase(bulkChangeCategoryGallery.pending, (state) => {
        state.loading.bulkChangeCategory = true;
        state.error.bulkChangeCategory = null;
      })
      .addCase(bulkChangeCategoryGallery.fulfilled, (state, action) => {
        state.loading.bulkChangeCategory = false;
        const { ids, category } = action.payload;
        const idSet = new Set(ids);
        state.images = state.images.map((img) =>
          idSet.has(img.id) ? { ...img, category } : img,
        );
      })
      .addCase(bulkChangeCategoryGallery.rejected, (state, action) => {
        state.loading.bulkChangeCategory = false;
        state.error.bulkChangeCategory =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to change category';
      });
  },
});

export const { setImages, appendImage, patchImage } = gallerySlice.actions;
export const galleryReducer = gallerySlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export interface GalleryRootState {
  gallery: GalleryState;
}

export const selectGalleryImages = (state: GalleryRootState): GalleryImageDTO[] =>
  state.gallery.images;

export const selectGalleryImagesByCategory = (
  state: GalleryRootState,
  category: GalleryImageCategory,
): GalleryImageDTO[] =>
  state.gallery.images.filter((img) => img.category === category);

export const selectGalleryLoading = (
  state: GalleryRootState,
): Record<GalleryLoadingKey, boolean> => state.gallery.loading;

export const selectGalleryError = (
  state: GalleryRootState,
): Record<GalleryLoadingKey, string | null> => state.gallery.error;
